<template>
  <div class="admin-dashboard-view">
    <!-- Hero -->
    <section class="dashboard-hero mb-4">
      <div
        class="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3"
      >
        <div>
          <div class="hero-eyebrow">Admin Dashboard</div>
          <h2 class="hero-title mb-1">{{ activeGroupName }} Control Center</h2>

          <div class="mt-3 d-flex flex-wrap gap-2 align-items-center">
            <span class="hero-pill hero-pill--group">
              <i class="bi bi-people-fill me-1"></i>
              {{ activeGroupName }}
            </span>

            <span v-if="selectedFinancialYear" class="hero-pill">
              <i class="bi bi-calendar-check me-1"></i>
              {{ selectedFinancialYear.year_key }}
            </span>

            <span class="hero-pill">
              <i class="bi bi-calendar-range me-1"></i>
              {{ niceDate(filters.from) }} → {{ niceDate(filters.to) }}
            </span>

            <span v-if="activeGroupRole" class="hero-pill">
              <i class="bi bi-person-badge me-1"></i>
              {{ formatGroupRole(activeGroupRole) }}
            </span>

            <span v-if="loading" class="hero-pill">
              <i class="bi bi-hourglass-split me-1"></i>
              Loading...
            </span>
          </div>
        </div>

        <div class="hero-filter-card">
          <div class="row g-2 align-items-end">
            <div class="col-12">
              <label class="form-label small text-muted mb-1">
                Financial Year
              </label>
              <select
                v-model="filters.financial_year_rule_id"
                class="form-select"
                :disabled="loading"
                @change="onFinancialYearChanged"
              >
                <option
                  v-for="fy in financialYears"
                  :key="fy.id"
                  :value="fy.id"
                >
                  {{ fy.year_key }} {{ fy.is_active ? "(active)" : "" }}
                </option>
              </select>
            </div>

            <div class="col-12 col-sm-5">
              <label class="form-label small text-muted mb-1">From</label>
              <input
                ref="fromInput"
                type="text"
                class="form-control"
                placeholder="Select date"
                readonly
              />
            </div>

            <div class="col-12 col-sm-5">
              <label class="form-label small text-muted mb-1">To</label>
              <input
                ref="toInput"
                type="text"
                class="form-control"
                placeholder="Select date"
                readonly
              />
            </div>

            <div class="col-12 col-sm-2 d-grid">
              <button class="btn btn-primary" @click="load" :disabled="loading">
                <i class="bi bi-arrow-repeat me-1"></i>
                {{ loading ? "Refreshing..." : "Refresh" }}
              </button>
            </div>
          </div>

          <div class="d-flex flex-wrap gap-2 mt-3">
            <button
              class="btn btn-light quick-btn"
              @click="setThisYear"
              :disabled="loading"
            >
              <i class="bi bi-calendar2-week me-1"></i>This Year
            </button>

            <button
              class="btn btn-light quick-btn"
              @click="setLast30Days"
              :disabled="loading"
            >
              <i class="bi bi-clock-history me-1"></i>Last 30 Days
            </button>
          </div>
        </div>
      </div>
    </section>

    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <!-- Top KPI cards -->
    <div class="row g-3 mb-4">
      <div class="col-md-6 col-xl-3">
        <div class="kpi-card kpi-card--blue h-100">
          <div class="d-flex justify-content-between align-items-start gap-3">
            <div>
              <div class="kpi-label">Total Members</div>
              <div class="kpi-value">{{ fmt(kpis.members.total) }}</div>
              <div class="kpi-note">Registered member accounts</div>
            </div>
            <div class="kpi-icon soft-blue">
              <i class="bi bi-people"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-xl-3">
        <div class="kpi-card kpi-card--indigo h-100">
          <div class="d-flex justify-content-between align-items-start gap-3">
            <div>
              <div class="kpi-label">Total Group Capital</div>
              <div class="kpi-value">
                {{ currency(totalGroupCapital) }}
              </div>
              <div class="kpi-note">
                Cash and assets less current liabilities
              </div>
            </div>
            <div class="kpi-icon soft-indigo">
              <i class="bi bi-wallet2"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-xl-3">
        <div class="kpi-card kpi-card--green h-100">
          <div class="d-flex justify-content-between align-items-start gap-3">
            <div>
              <div class="kpi-label">Total Group Income</div>
              <div class="kpi-value text-success">
                {{ currency(totalGroupIncome) }}
              </div>
              <div class="kpi-note">Earned income in the selected period</div>
              <button
                type="button"
                class="btn btn-sm btn-link px-0 mt-2 income-details-link"
                @click="showIncomeDetails = true"
              >
                View income details <i class="bi bi-arrow-right ms-1"></i>
              </button>
            </div>
            <div class="kpi-icon soft-green">
              <i class="bi bi-graph-up-arrow"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-xl-3">
        <div class="kpi-card kpi-card--amber h-100">
          <div class="d-flex justify-content-between align-items-start gap-3">
            <div>
              <div class="kpi-label">Loans Outstanding</div>
              <div class="kpi-value">
                {{ currency(kpis.loans.outstanding_total) }}
              </div>
              <div class="kpi-note">Current unpaid loan total</div>
            </div>
            <div class="kpi-icon soft-amber">
              <i class="bi bi-bank"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-xl-3">
        <div class="kpi-card kpi-card--red h-100">
          <div class="d-flex justify-content-between align-items-start gap-3">
            <div>
              <div class="kpi-label">Pending Withdrawals</div>
              <div class="kpi-value">
                {{ fmt(kpis.withdrawals.pending_count) }}
              </div>
              <div class="kpi-note">Awaiting action</div>
            </div>
            <div class="kpi-icon soft-red">
              <i class="bi bi-cash-stack"></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Financial snapshot -->
    <div class="row g-3 mb-4">
      <div class="col-lg-8">
        <div class="card dashboard-card h-100">
          <div
            class="card-header bg-white d-flex justify-content-between align-items-center"
          >
            <strong>Financial Statement</strong>
            <span class="text-muted small">
              {{ niceDate(filters.from) }} → {{ niceDate(filters.to) }}
            </span>
          </div>

          <div class="card-body">
            <div class="summary-grid">
              <div class="summary-item">
                <div class="small text-muted">Opening Position</div>
                <div class="summary-value">
                  {{ currency(kpis.balances.opening_position) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Contributions In</div>
                <div class="summary-value">
                  {{ currency(period.contributions_in) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Loan Repayment In</div>
                <div class="summary-value">
                  {{ currency(period.loan_repayment_cash_in) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Profit In</div>
                <div class="summary-value">
                  {{ currency(period.profit_in) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Upfront Loan Interest</div>
                <div class="summary-value text-success">
                  {{ currency(period.loan_interest_income) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Total Group Income</div>
                <div class="summary-value text-success">
                  {{ currency(totalGroupIncome) }}
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-link px-0 mt-1 income-details-link"
                  @click="showIncomeDetails = true"
                >
                  View details
                </button>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Penalty Cash In</div>
                <div class="summary-value">
                  {{ currency(period.penalty_cash_in) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Other Income In</div>
                <div class="summary-value text-success">
                  {{ currency(period.income_in) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Loan Disbursed Out</div>
                <div class="summary-value">
                  {{ currency(period.loan_disbursed_out) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Expenses Out</div>
                <div class="summary-value text-danger">
                  {{ currency(period.expenses_out) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Opening Loan Migrated</div>
                <div class="summary-value text-warning">
                  {{ currency(period.opening_loan_migrated) }}
                </div>
                <div class="small text-muted">Historical receivable</div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Withdrawals Out</div>
                <div class="summary-value">
                  {{ currency(period.withdrawals_out) }}
                </div>
              </div>

              <div class="summary-item summary-item--accent">
                <div class="small text-muted">Net Cash Flow</div>

                <div
                  class="summary-value"
                  :class="netFlow >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ netFlow >= 0 ? "+" : "-" }}
                  {{ fmt(Math.abs(netFlow)) }} RWF
                </div>

                <div class="small text-muted">
                  Excludes migrated opening loans
                </div>
              </div>

              <div class="summary-item summary-item--accent">
                <div class="small text-muted">Closing Position</div>
                <div class="summary-value">
                  {{ currency(kpis.balances.cash_position) }}
                </div>
                <div class="small text-muted">
                  Closing cash position for the selected period
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Loan Receivables</div>
                <div class="summary-value">
                  {{ currency(groupCapital.assets.loan_receivables) }}
                </div>
                <div class="small text-muted">
                  Current outstanding principal
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Gross Group Assets</div>
                <div class="summary-value">
                  {{ currency(groupCapital.gross_group_assets) }}
                </div>
                <div class="small text-muted">
                  Cash, loans, investments and other assets
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Total Liabilities</div>
                <div
                  class="summary-value"
                  :class="
                    Number(groupCapital.total_liabilities || 0) > 0
                      ? 'text-danger'
                      : ''
                  "
                >
                  {{ currency(groupCapital.total_liabilities) }}
                </div>
                <div class="small text-muted">
                  Approved withdrawals and other obligations
                </div>
              </div>

              <div class="summary-item summary-item--capital">
                <div class="small text-muted">Total Group Capital</div>
                <div class="summary-value text-primary">
                  {{ currency(totalGroupCapital) }}
                </div>
                <div class="small text-muted">
                  Gross assets less liabilities
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Net Group Profit</div>

                <div
                  class="summary-value"
                  :class="netGroupProfit >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ currency(netGroupProfit) }}
                </div>

                <div class="small text-muted">Total income less expenses</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-4">
        <div class="card dashboard-card h-100">
          <div class="card-header bg-white">
            <strong>Attention Needed</strong>
          </div>

          <div class="card-body">
            <div class="health-list">
              <div class="health-row">
                <span class="text-muted small">Pending Loan Applications</span>
                <strong>{{ attention.pending_loan_applications }}</strong>
              </div>

              <div class="health-row">
                <span class="text-muted small">Pending Withdrawals</span>
                <strong class="text-danger">
                  {{ attention.pending_withdrawals }}
                </strong>
              </div>

              <div class="health-row">
                <span class="text-muted small">Awaiting Withdrawals</span>
                <strong>{{ attention.approved_withdrawals }}</strong>
              </div>

              <div class="health-row">
                <span class="text-muted small">Queued for FY Close</span>
                <strong>{{ attention.queued_withdrawals }}</strong>
              </div>

              <div class="health-row">
                <span class="text-muted small">Overdue Loans</span>
                <strong
                  :class="
                    attention.overdue_loans > 0 ? 'text-danger' : 'text-success'
                  "
                >
                  {{ attention.overdue_loans }}
                </strong>
              </div>

              <div class="health-row">
                <span class="text-muted small">Unpaid Penalties</span>
                <strong
                  :class="
                    attention.unpaid_penalties > 0
                      ? 'text-danger'
                      : 'text-success'
                  "
                >
                  {{ attention.unpaid_penalties }}
                </strong>
              </div>

              <div class="health-row">
                <span class="text-muted small">
                  Members with Unpaid Penalties
                </span>
                <strong>{{ attention.members_with_unpaid_penalties }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Portfolio + penalties -->
    <div class="row g-3 mb-4">
      <div class="col-lg-6">
        <div class="card dashboard-card h-100">
          <div class="card-header bg-white">
            <strong>Loan Portfolio</strong>
          </div>

          <div class="card-body">
            <div class="summary-grid summary-grid--compact">
              <div class="summary-item">
                <div class="small text-muted">Active Loans</div>
                <div class="summary-value">
                  {{ fmt(kpis.loans.active_count) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Defaulted Loans</div>
                <div class="summary-value">
                  {{ fmt(kpis.loans.defaulted_count) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Overdue Loans</div>
                <div class="summary-value">
                  {{ fmt(kpis.loans.overdue_count) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Outstanding Principal</div>
                <div class="summary-value">
                  {{ currency(kpis.loans.outstanding_principal) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Outstanding Interest</div>
                <div class="summary-value">
                  {{ currency(kpis.loans.outstanding_interest) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Pending Applications</div>
                <div class="summary-value">
                  {{ fmt(kpis.loans.pending_applications_count) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-6">
        <div class="card dashboard-card h-100">
          <div class="card-header bg-white">
            <strong>Penalty Overview</strong>
          </div>

          <div class="card-body">
            <div class="summary-grid summary-grid--compact">
              <div class="summary-item">
                <div class="small text-muted">Unpaid Count</div>
                <div class="summary-value">
                  {{ fmt(kpis.penalties.unpaid_count) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Unpaid Amount</div>
                <div class="summary-value text-danger">
                  {{ currency(kpis.penalties.unpaid_amount) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Affected Members</div>
                <div class="summary-value">
                  {{ fmt(kpis.penalties.members_with_unpaid_penalties) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Created This Period</div>
                <div class="summary-value">
                  {{ fmt(periodPenalties.created_count) }}
                </div>
                <div class="small text-muted mt-1">
                  {{ currency(periodPenalties.created_amount) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Paid This Period</div>
                <div class="summary-value">
                  {{ fmt(periodPenalties.paid_count) }}
                </div>
                <div class="small text-muted mt-1">
                  {{ currency(periodPenalties.paid_amount) }}
                </div>
              </div>

              <div class="summary-item">
                <div class="small text-muted">Waived This Period</div>
                <div class="summary-value">
                  {{ fmt(periodPenalties.waived_count) }}
                </div>
                <div class="small text-muted mt-1">
                  {{ currency(periodPenalties.waived_amount) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent activity -->
    <div class="card dashboard-card">
      <div
        class="card-header bg-white d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2"
      >
        <div>
          <strong>Recent Activity</strong>
          <div class="text-muted small">
            Latest 15 transactions in the selected group and date range
          </div>
        </div>

        <span class="text-muted small">{{ recent.length }} record(s)</span>
      </div>

      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table dashboard-table align-middle mb-0">
            <thead>
              <tr>
                <th style="width: 16%">Date</th>
                <th style="width: 18%">Account / Member</th>
                <th style="width: 15%">Direction</th>
                <th style="width: 18%">Type</th>
                <th>Reference</th>
                <th style="width: 16%" class="text-end">Amount</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="t in recent" :key="t.id">
                <td class="text-muted">
                  {{ fmtDateTime(t.transaction_date || t.created_at) }}
                </td>

                <td>
                  <div class="fw-semibold text-dark">
                    {{ activityOwnerName(t) }}
                  </div>
                  <div class="small text-muted">
                    {{ activityOwnerSubtext(t) }}
                  </div>
                </td>

                <td>
                  <span
                    v-if="Number(t.credit) > 0"
                    class="badge text-bg-success"
                  >
                    <i class="bi bi-plus-circle me-1"></i>IN
                  </span>
                  <span v-else class="badge text-bg-danger">
                    <i class="bi bi-dash-circle me-1"></i>OUT
                  </span>
                </td>

                <td>
                  <span class="type-pill">{{ humanType(t.type) }}</span>
                </td>

                <td class="text-muted">{{ cleanReference(t.reference) }}</td>

                <td class="text-end fw-semibold">
                  <span v-if="Number(t.credit) > 0" class="text-success">
                    +{{ fmt(t.credit) }}
                  </span>
                  <span v-else class="text-danger">-{{ fmt(t.debit) }}</span>
                  <span class="text-muted"> RWF</span>
                </td>
              </tr>

              <tr v-if="!loading && recent.length === 0">
                <td colspan="6" class="text-center text-muted py-4">
                  No activity yet
                </td>
              </tr>

              <tr v-if="loading">
                <td colspan="6" class="text-center text-muted py-4">
                  Loading...
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div
      v-if="showIncomeDetails"
      class="income-modal-backdrop"
      @click.self="showIncomeDetails = false"
    >
      <div class="card income-modal-card shadow-lg">
        <div
          class="card-header bg-white d-flex justify-content-between align-items-center"
        >
          <div>
            <strong>Group Income Details</strong>
            <div class="text-muted small">
              {{ niceDate(filters.from) }} → {{ niceDate(filters.to) }}
            </div>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="showIncomeDetails = false"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        <div class="card-body">
          <div class="income-detail-list">
            <div class="income-detail-row">
              <div>
                <div class="fw-semibold">Upfront Loan Interest</div>
                <div class="small text-muted">
                  Interest deducted when loans were disbursed
                </div>
              </div>
              <strong>{{ currency(incomeDetails.loan_interest) }}</strong>
            </div>
            <div class="income-detail-row">
              <div>
                <div class="fw-semibold">Penalty Income</div>
                <div class="small text-muted">
                  Penalties actually paid during the selected period
                </div>
              </div>
              <strong>{{ currency(incomeDetails.penalties) }}</strong>
            </div>
            <div class="income-detail-row">
              <div>
                <div class="fw-semibold">Other Income</div>
                <div class="small text-muted">General income transactions</div>
              </div>
              <strong>{{ currency(incomeDetails.other_income) }}</strong>
            </div>
          </div>
          <div class="income-total-box mt-3">
            <span>Gross Group Income</span>

            <strong>
              {{ currency(totalGroupIncome) }}
            </strong>
          </div>

          <div class="income-detail-row mt-3">
            <div>
              <div class="fw-semibold text-danger">Less Expenses</div>

              <div class="small text-muted">
                Operational expenses in the selected period
              </div>
            </div>

            <strong class="text-danger">
              -{{ currency(period.expenses_out) }}
            </strong>
          </div>

          <div
            class="income-total-box mt-3"
            :class="netGroupProfit < 0 ? 'income-total-box--loss' : ''"
          >
            <span>Net Group Profit</span>

            <strong>
              {{ currency(netGroupProfit) }}
            </strong>
          </div>          
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDashboard } from "@/services/dashboard";
import http from "@/services/http";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.css";

export default {
  name: "AdminDashboardView",

  data() {
    const today = new Date().toISOString().slice(0, 10);
    const y = new Date().getFullYear();
    const start = `${y}-01-01`;

    return {
      loading: false,
      error: "",
      showIncomeDetails: false,
      filters: {
        financial_year_rule_id: "",
        from: start,
        to: today,
      },

      financialYears: [],
      activeFinancialYearId: null,

      kpis: this.defaultKpis(),
      groupCapital: this.defaultGroupCapital(),
      cashflow: this.defaultCashflow(),
      attention: this.defaultAttention(),
      periodPenalties: this.defaultPeriodPenalties(),
      recent: [],

      fpFrom: null,
      fpTo: null,
    };
  },

  computed: {
    selectedFinancialYear() {
      return (
        this.financialYears.find(
          (fy) => String(fy.id) === String(this.filters.financial_year_rule_id),
        ) || null
      );
    },

    activeFinancialYear() {
      return this.financialYears.find((fy) => Boolean(fy.is_active)) || null;
    },

    activeGroupName() {
      return localStorage.getItem("active_group_name") || "Selected Group";
    },

    activeGroupRole() {
      return localStorage.getItem("active_group_role") || "";
    },

    period() {
      return this.kpis?.period || {};
    },

    netFlow() {
      return Number(this.period.cash_net_flow || 0);
    },

    incomeDetails() {
      const b = this.period?.income_breakdown || {};
      return {
        loan_interest: Number(
          b.loan_interest ??
            b.loan_interest_deducted ??
            this.period.loan_interest_income ??
            0,
        ),
        penalties: Number(
          b.penalties ?? b.penalty_income ?? this.period.penalty_cash_in ?? 0,
        ),
        other_income: Number(b.other_income ?? this.period.income_in ?? 0),
        profit: Number(
          b.profit ?? b.profit_income ?? this.period.profit_in ?? 0,
        ),
      };
    },

    totalGroupIncome() {
      if (this.period?.total_income !== undefined)
        return Number(this.period.total_income || 0);
      return (
        this.incomeDetails.loan_interest +
        this.incomeDetails.penalties +
        this.incomeDetails.other_income +
        this.incomeDetails.profit
      );
    },
    totalGroupCapital() {
      return Number(
        this.groupCapital?.total_group_capital ??
          this.kpis?.balances?.total_group_capital ??
          this.kpis?.balances?.system_net_balance ??
          0,
      );
    },

    netGroupProfit() {
      return Number(
        this.period.net_group_profit ??
          this.totalGroupIncome - Number(this.period.expenses_out || 0),
      );
    },
  },

  async mounted() {
    await this.loadFinancialYears();

    this.$nextTick(() => {
      this.initDatePickers();
      this.syncDatePickers();
    });

    if (this.filters.financial_year_rule_id) {
      await this.load();
    }
  },

  beforeUnmount() {
    this.fpFrom?.destroy?.();
    this.fpTo?.destroy?.();
    this.fpFrom = null;
    this.fpTo = null;
  },

  methods: {
    defaultKpis() {
      return {
        members: {
          total: 0,
        },
        balances: {
          system_net_balance: 0,
          total_group_capital: 0,
          gross_group_assets: 0,
          total_liabilities: 0,
          opening_position: 0,
          cash_position: 0,
          loan_receivables: 0,
          ledger_net_balance: 0,
          opening_balances_total: 0,
        },
        loans: {
          active_count: 0,
          defaulted_count: 0,
          overdue_count: 0,
          outstanding_total: 0,
          outstanding_principal: 0,
          outstanding_interest: 0,
          pending_applications_count: 0,
        },
        penalties: {
          unpaid_count: 0,
          unpaid_amount: 0,
          members_with_unpaid_penalties: 0,
        },
        withdrawals: {
          pending_count: 0,
          approved_count: 0,
          queued_for_fy_close_count: 0,
          paid_count: 0,
          paid_total: 0,
        },
        period: {
          opening_balance_in: 0,
          contributions_in: 0,
          loan_repayment_cash_in: 0,
          profit_in: 0,
          loan_interest_income: 0,
          penalty_cash_in: 0,
          income_in: 0,
          total_income: 0,
          income_breakdown: {
            loan_interest: 0,
            penalties: 0,
            other_income: 0,
            profit: 0,
          },
          loan_disbursed_out: 0,
          expenses_out: 0,
          opening_loan_migrated: 0,
          withdrawals_out: 0,
          cash_net_flow: 0,
          period_closing_position: 0,
          balance_sheet_movement: 0,
          penalties_created_count: 0,
          penalties_created_amount: 0,
          penalties_paid_count: 0,
          penalties_paid_amount: 0,
          penalties_waived_count: 0,
          penalties_waived_amount: 0,
        },
      };
    },

    defaultGroupCapital() {
      return {
        as_of_date: null,
        valuation_basis: {
          cash: "selected_range",
          loan_receivables: "current_outstanding_principal",
          future_interest_included: false,
        },
        assets: {
          cash_and_equivalents: 0,
          loan_receivables: 0,
          investments: 0,
          other_receivables: 0,
          other_assets: 0,
        },
        liabilities: {
          approved_withdrawals: 0,
          other_liabilities: 0,
        },
        gross_group_assets: 0,
        total_liabilities: 0,
        total_group_capital: 0,
      };
    },

    defaultCashflow() {
      return {
        opening_balance_in: 0,
        contributions_in: 0,
        loan_repayment_in: 0,
        profit_in: 0,
        penalty_cash_in: 0,
        income_in: 0,
        loan_disbursed_out: 0,
        expenses_out: 0,
        opening_loan_migrated: 0,
        withdrawals_out: 0,
        cash_net_flow: 0,
        balance_sheet_movement: 0,
      };
    },

    defaultAttention() {
      return {
        pending_loan_applications: 0,
        pending_withdrawals: 0,
        approved_withdrawals: 0,
        queued_withdrawals: 0,
        overdue_loans: 0,
        unpaid_penalties: 0,
        members_with_unpaid_penalties: 0,
      };
    },

    defaultPeriodPenalties() {
      return {
        created_count: 0,
        created_amount: 0,
        paid_count: 0,
        paid_amount: 0,
        waived_count: 0,
        waived_amount: 0,
      };
    },

    async loadFinancialYears() {
      try {
        const response = await http.get("/financial-year-rules");

        const raw = response.data?.data ?? response.data ?? [];

        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
          ? raw.data
          : [];

        this.financialYears = [...list].sort(
          (a, b) =>
            Number(Boolean(b.is_active)) - Number(Boolean(a.is_active)) ||
            String(b.start_date || "").localeCompare(
              String(a.start_date || ""),
            ),
        );

        const active =
          this.financialYears.find((fy) => Boolean(fy.is_active)) ||
          this.financialYears[0] ||
          null;

        this.activeFinancialYearId = active?.id || null;

        const saved = localStorage.getItem("admin_dashboard_financial_year");

        const savedExists =
          saved &&
          this.financialYears.some((fy) => String(fy.id) === String(saved));

        this.filters.financial_year_rule_id = savedExists
          ? Number(saved)
          : this.activeFinancialYearId || "";

        this.applySelectedFinancialYearRange();
      } catch (error) {
        this.error =
          error?.response?.data?.message || "Failed to load financial years.";

        this.financialYears = [];
        this.activeFinancialYearId = null;
        this.filters.financial_year_rule_id = "";
      }
    },

    applySelectedFinancialYearRange() {
      const financialYear = this.selectedFinancialYear;

      if (!financialYear) {
        this.filters.from = "";
        this.filters.to = "";
        return;
      }

      this.filters.from = String(financialYear.start_date).slice(0, 10);

      this.filters.to = String(financialYear.end_date).slice(0, 10);
    },

    async onFinancialYearChanged() {
      localStorage.setItem(
        "admin_dashboard_financial_year",
        String(this.filters.financial_year_rule_id || ""),
      );

      const financialYear = this.selectedFinancialYear;

      if (!financialYear) {
        return;
      }

      this.filters.from = String(financialYear.start_date).slice(0, 10);

      this.filters.to = String(financialYear.end_date).slice(0, 10);

      await this.$nextTick();

      this.initDatePickers();
      this.syncDatePickers();

      await this.load();
    },

    updateDatePickerLimits() {
      const financialYear = this.selectedFinancialYear;

      if (!financialYear) {
        return;
      }

      const start = String(financialYear.start_date).slice(0, 10);

      const end = String(financialYear.end_date).slice(0, 10);

      this.fpFrom?.set("minDate", start);
      this.fpFrom?.set("maxDate", end);

      this.fpTo?.set("minDate", start);
      this.fpTo?.set("maxDate", end);
    },

    fmt(n) {
      return Number(n || 0).toLocaleString();
    },

    currency(n) {
      return `${this.fmt(n)} RWF`;
    },

    formatGroupRole(role) {
      if (!role) return "";

      return String(role)
        .replaceAll("_", " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },

    niceDate(dt) {
      if (!dt) return "-";

      const d = new Date(dt);

      if (isNaN(d.getTime())) return String(dt);

      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");

      return `${yyyy}-${mm}-${dd}`;
    },

    fmtDateTime(dt) {
      if (!dt) return "-";

      const s = String(dt);

      return s.includes("T")
        ? s.replace("T", " ").slice(0, 19)
        : s.slice(0, 19);
    },

    humanType(type) {
      const labels = {
        opening_balance: "Opening Balance",
        opening_savings: "Opening Savings",
        opening_loan: "Opening Loan",
        opening_loan_reversal: "Opening Loan Reversal",
        opening_loan_undo: "Opening Loan Undo",

        contribution: "Contribution",
        contribution_reversal: "Contribution Reversal",
        contribution_undo: "Contribution Undo",

        profit: "Profit",
        profit_reversal: "Profit Reversal",
        profit_undo: "Profit Undo",
        profit_withdrawal: "Profit Withdrawal",
        profit_withdrawal_reversal: "Profit Withdrawal Reversal",
        profit_withdrawal_undo: "Profit Withdrawal Undo",

        income: "Income",
        income_reversal: "Income Reversal",
        income_undo: "Income Undo",

        expense: "Expense",
        expense_reversal: "Expense Reversal",
        expense_undo: "Expense Undo",

        loan_interest_deducted: "Upfront Loan Interest",
        loan_interest_deducted_reversal: "Loan Interest Reversal",

        loan_disbursement: "Loan Disbursement",
        loan_disbursement_reversal: "Loan Disbursement Reversal",
        loan_disbursement_undo: "Loan Disbursement Undo",

        loan_repayment: "Loan Repayment",
        loan_repayment_reversal: "Loan Repayment Reversal",
        loan_repayment_undo: "Loan Repayment Undo",

        penalty_paid: "Penalty Payment",
        penalty_paid_reversal: "Penalty Payment Reversal",
        penalty_paid_undo: "Penalty Payment Undo",

        member_withdrawal: "Member Withdrawal",
        member_exit_withdrawal: "Exit Withdrawal",
        member_withdrawal_reversal: "Member Withdrawal Reversal",
        member_exit_withdrawal_reversal: "Exit Withdrawal Reversal",
      };

      return labels[type] || (type ? String(type).replaceAll("_", " ") : "-");
    },

    isGroupActivity(type) {
      return [
        "income",
        "income_reversal",
        "income_undo",
        "expense",
        "expense_reversal",
        "expense_undo",
        "profit",
        "profit_reversal",
        "profit_undo",
        "opening_balance",
        "opening_balance_reversal",
        "opening_balance_undo",
      ].includes(type);
    },

    activityOwnerName(t) {
      if (this.isGroupActivity(t.type)) {
        return this.activeGroupName;
      }

      return t.member_name || "-";
    },

    activityOwnerSubtext(t) {
      if (this.isGroupActivity(t.type)) {
        return "Group transaction";
      }

      return t.member_email || "";
    },

    cleanReference(reference) {
      if (!reference) return "-";

      return String(reference).replace(" - ", " — ");
    },

    initDatePickers() {
      const fromEl = this.$refs.fromInput;
      const toEl = this.$refs.toInput;
      const financialYear = this.selectedFinancialYear;

      if (!financialYear) {
        return;
      }

      const minDate = String(financialYear.start_date).slice(0, 10);

      const maxDate = String(financialYear.end_date).slice(0, 10);

      if (fromEl) {
        this.fpFrom?.destroy?.();

        this.fpFrom = flatpickr(fromEl, {
          dateFormat: "Y-m-d",
          defaultDate: this.filters.from || minDate,
          disableMobile: true,
          allowInput: false,
          minDate,
          maxDate,

          onChange: (_, dateStr) => {
            this.filters.from = dateStr;

            if (this.filters.to && dateStr > this.filters.to) {
              this.filters.to = dateStr;

              this.fpTo?.setDate?.(dateStr, false, "Y-m-d");
            }
          },
        });
      }

      if (toEl) {
        this.fpTo?.destroy?.();

        this.fpTo = flatpickr(toEl, {
          dateFormat: "Y-m-d",
          defaultDate: this.filters.to || maxDate,
          disableMobile: true,
          allowInput: false,
          minDate,
          maxDate,

          onChange: (_, dateStr) => {
            this.filters.to = dateStr;

            if (this.filters.from && dateStr < this.filters.from) {
              this.filters.from = dateStr;

              this.fpFrom?.setDate?.(dateStr, false, "Y-m-d");
            }
          },
        });
      }

      this.syncDatePickers();
    },
    syncDatePickers() {
      this.fpFrom?.setDate?.(this.filters.from, false, "Y-m-d");

      this.fpTo?.setDate?.(this.filters.to, false, "Y-m-d");
    },

    setThisYear() {
      const fy = this.selectedFinancialYear;

      if (!fy) return;

      const start = String(fy.start_date || "").slice(0, 10);

      const end = String(fy.end_date || "").slice(0, 10);

      this.filters.from = start;
      this.filters.to = end;

      this.fpFrom?.setDate?.(start, true, "Y-m-d");
      this.fpTo?.setDate?.(end, true, "Y-m-d");

      this.load();
    },

    setLast30Days() {
      const fy = this.selectedFinancialYear;

      if (!fy) return;

      const fyStart = new Date(
        `${String(fy.start_date).slice(0, 10)}T00:00:00`,
      );

      const fyEnd = new Date(`${String(fy.end_date).slice(0, 10)}T00:00:00`);

      const today = new Date();
      const end =
        today < fyStart
          ? new Date(fyStart)
          : today > fyEnd
          ? new Date(fyEnd)
          : today;

      const start = new Date(end);
      start.setDate(start.getDate() - 30);

      if (start < fyStart) {
        start.setTime(fyStart.getTime());
      }

      const from = this.toDateKey(start);

      const to = this.toDateKey(end);

      this.filters.from = from;
      this.filters.to = to;

      this.fpFrom?.setDate?.(from, true, "Y-m-d");
      this.fpTo?.setDate?.(to, true, "Y-m-d");

      this.load();
    },

    toDateKey(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },

    async load() {
      this.error = "";

      const financialYear = this.selectedFinancialYear;

      if (!financialYear) {
        this.error = "Select a financial year.";
        return;
      }

      const yearStart = String(financialYear.start_date || "").slice(0, 10);

      const yearEnd = String(financialYear.end_date || "").slice(0, 10);

      if (
        !this.filters.from ||
        this.filters.from < yearStart ||
        this.filters.from > yearEnd
      ) {
        this.filters.from = yearStart;
      }

      if (
        !this.filters.to ||
        this.filters.to < yearStart ||
        this.filters.to > yearEnd
      ) {
        this.filters.to = yearEnd;
      }

      if (this.filters.from > this.filters.to) {
        this.filters.from = yearStart;
        this.filters.to = yearEnd;
      }

      this.fpFrom?.setDate?.(this.filters.from, false, "Y-m-d");

      this.fpTo?.setDate?.(this.filters.to, false, "Y-m-d");

      this.loading = true;

      try {
        const res = await getDashboard({
          financial_year_rule_id: this.filters.financial_year_rule_id,
          from: this.filters.from,
          to: this.filters.to,
        });

        const payload = res?.data || {};
        const defaults = this.defaultKpis();

        if (payload?.filters) {
          this.filters.from = payload.filters.from || this.filters.from;

          this.filters.to = payload.filters.to || this.filters.to;

          this.fpFrom?.setDate?.(this.filters.from, false, "Y-m-d");

          this.fpTo?.setDate?.(this.filters.to, false, "Y-m-d");
        }

        const currentState = payload?.current_state || {};

        const financialYearKpis = payload?.financial_year_kpis || {};

        const legacyKpis = payload?.kpis || {};

        const capitalPayload = payload?.group_capital || {};

        this.kpis = {
          ...defaults,
          ...legacyKpis,
          members: {
            ...defaults.members,
            ...(legacyKpis?.members || {}),
            ...(currentState?.members || {}),
          },
          balances: {
            ...defaults.balances,
            ...(legacyKpis?.balances || {}),
            ...(financialYearKpis?.balances || {}),
          },
          loans: {
            ...defaults.loans,
            ...(legacyKpis?.loans || {}),
            ...(currentState?.loans || {}),
          },
          penalties: {
            ...defaults.penalties,
            ...(legacyKpis?.penalties || {}),
            ...(currentState?.penalties || {}),
          },
          withdrawals: {
            ...defaults.withdrawals,
            ...(legacyKpis?.withdrawals || {}),
            ...(currentState?.withdrawals || {}),
          },
          period: {
            ...defaults.period,
            ...(legacyKpis?.period || {}),
            ...(financialYearKpis?.period || {}),
          },
        };

        const capitalDefaults = this.defaultGroupCapital();

        this.groupCapital = {
          ...capitalDefaults,
          ...capitalPayload,
          assets: {
            ...capitalDefaults.assets,
            ...(capitalPayload?.assets || {}),
          },
          liabilities: {
            ...capitalDefaults.liabilities,
            ...(capitalPayload?.liabilities || {}),
          },
          valuation_basis: {
            ...capitalDefaults.valuation_basis,
            ...(capitalPayload?.valuation_basis || {}),
          },
        };

        /*
         * Compatibility fallback while older backend responses are still
         * possible during deployment.
         */
        if (
          this.groupCapital.total_group_capital === 0 &&
          this.kpis.balances.total_group_capital !== undefined
        ) {
          this.groupCapital.total_group_capital = Number(
            this.kpis.balances.total_group_capital || 0,
          );
        }

        if (
          this.groupCapital.gross_group_assets === 0 &&
          this.kpis.balances.gross_group_assets !== undefined
        ) {
          this.groupCapital.gross_group_assets = Number(
            this.kpis.balances.gross_group_assets || 0,
          );
        }

        if (
          this.groupCapital.total_liabilities === 0 &&
          this.kpis.balances.total_liabilities !== undefined
        ) {
          this.groupCapital.total_liabilities = Number(
            this.kpis.balances.total_liabilities || 0,
          );
        }

        if (
          this.groupCapital.assets.cash_and_equivalents === 0 &&
          this.kpis.balances.cash_position !== undefined
        ) {
          this.groupCapital.assets.cash_and_equivalents = Number(
            this.kpis.balances.cash_position || 0,
          );
        }

        if (
          this.groupCapital.assets.loan_receivables === 0 &&
          this.kpis.balances.loan_receivables !== undefined
        ) {
          this.groupCapital.assets.loan_receivables = Number(
            this.kpis.balances.loan_receivables || 0,
          );
        }

        const period = this.kpis.period || {};

        this.cashflow = {
          opening_balance_in: period.opening_balance_in || 0,
          contributions_in: period.contributions_in || 0,
          loan_repayment_in: period.loan_repayment_cash_in || 0,
          profit_in: period.profit_in || 0,
          loan_interest_income: period.loan_interest_income || 0,
          penalty_cash_in: period.penalty_cash_in || 0,
          income_in: period.income_in || 0,

          loan_disbursed_out: period.loan_disbursed_out || 0,
          expenses_out: period.expenses_out || 0,
          opening_loan_migrated: period.opening_loan_migrated || 0,
          withdrawals_out: period.withdrawals_out || 0,

          cash_net_flow: period.cash_net_flow || 0,
          balance_sheet_movement: period.balance_sheet_movement || 0,
        };

        this.attention = {
          ...this.defaultAttention(),
          ...(payload?.attention || {}),
        };

        this.periodPenalties = {
          ...this.defaultPeriodPenalties(),
          ...(payload?.period_penalties || {}),
        };

        this.recent = Array.isArray(payload?.recent_transactions)
          ? payload.recent_transactions
          : [];
      } catch (e) {
        const errors = e?.response?.data?.errors;

        const firstError = errors ? Object.values(errors)?.[0]?.[0] : null;

        this.error =
          firstError ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to load dashboard";

        this.kpis = this.defaultKpis();
        this.groupCapital = this.defaultGroupCapital();
        this.cashflow = this.defaultCashflow();
        this.attention = this.defaultAttention();
        this.periodPenalties = this.defaultPeriodPenalties();
        this.recent = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-dashboard-view {
  color: #0f172a;
}

.dashboard-hero {
  padding: 24px;
  border-radius: 26px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.14),
    rgba(16, 185, 129, 0.1)
  );
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.05);
}

.hero-eyebrow {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #2563eb;
  font-weight: 700;
  margin-bottom: 6px;
}

.hero-title {
  font-weight: 800;
  color: #0f172a;
}

.hero-text {
  color: #475569;
  max-width: 760px;
}

.hero-pill {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 0.9rem;
}

.hero-subtitle {
  color: #475569;
  font-size: 0.95rem;
  margin-top: 4px;
}

.hero-pill--group {
  background: rgba(37, 99, 235, 0.1);
  border-color: rgba(37, 99, 235, 0.2);
  color: #1d4ed8;
  font-weight: 700;
}

.hero-filter-card {
  width: 100%;
  max-width: 520px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 14px;
}

.quick-btn {
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: rgba(255, 255, 255, 0.9);
}

.dashboard-card {
  border: 0;
  border-radius: 22px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
  overflow: hidden;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.dashboard-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
}

.kpi-card {
  position: relative;
  padding: 22px;
  border-radius: 22px;
  background: linear-gradient(180deg, #ffffff, #f8fafc);
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
}

.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 20px 36px rgba(15, 23, 42, 0.08);
}

.kpi-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  border-top-left-radius: 22px;
  border-top-right-radius: 22px;
}

.kpi-card--blue::before {
  background: linear-gradient(90deg, #2563eb, #38bdf8);
}

.kpi-card--indigo::before {
  background: linear-gradient(90deg, #4f46e5, #2563eb);
}

.kpi-card--amber::before {
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
}

.kpi-card--red::before {
  background: linear-gradient(90deg, #ef4444, #f97316);
}

.kpi-label {
  font-size: 0.92rem;
  color: #64748b;
  margin-bottom: 8px;
}

.kpi-value {
  font-size: clamp(1.05rem, 1.35vw, 1.5rem);
  font-weight: 800;
  line-height: 1.25;
  color: #0f172a;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.kpi-note {
  margin-top: 8px;
  font-size: 0.9rem;
  color: #64748b;
}

.kpi-icon {
  width: 54px;
  height: 54px;
  min-width: 54px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.soft-blue {
  background: rgba(37, 99, 235, 0.12);
  color: #2563eb;
}

.soft-indigo {
  background: rgba(79, 70, 229, 0.12);
  color: #4f46e5;
}

.soft-amber {
  background: rgba(245, 158, 11, 0.14);
  color: #d97706;
}

.soft-red {
  background: rgba(220, 38, 38, 0.12);
  color: #dc2626;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
}

.summary-grid--compact {
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
}

.summary-item {
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: linear-gradient(180deg, #ffffff, #fbfdff);
}

.summary-item--accent {
  box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.08);
}

.summary-item--capital {
  border-color: rgba(37, 99, 235, 0.25);
  background: linear-gradient(
    180deg,
    rgba(37, 99, 235, 0.08),
    rgba(255, 255, 255, 0.96)
  );
}

.summary-value {
  font-weight: 700;
  color: #0f172a;
}

.health-list {
  display: grid;
  gap: 12px;
}

.health-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #eef2f7;
  border-radius: 16px;
  background: #ffffff;
}

.table-responsive {
  border-radius: 20px;
  overflow: hidden;
}

.dashboard-table thead {
  background: rgba(59, 130, 246, 0.08);
}

.dashboard-table thead th {
  border-bottom: 0;
  color: #1e40af;
  font-weight: 600;
}

.dashboard-table tbody tr:hover {
  background: rgba(15, 23, 42, 0.04);
}

.type-pill {
  display: inline-block;
  padding: 7px 12px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
}

.form-control,
.btn {
  border-radius: 12px;
}

.kpi-card .d-flex {
  min-width: 0;
}

.kpi-card .d-flex > div:first-child {
  min-width: 0;
  flex: 1;
}

.kpi-card--green::before {
  background: linear-gradient(90deg, #059669, #34d399);
}
.soft-green {
  background: rgba(5, 150, 105, 0.12);
  color: #059669;
}
.income-details-link {
  color: #047857;
  text-decoration: none;
  font-weight: 600;
}
.income-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(3px);
}
.income-modal-card {
  width: 100%;
  max-width: 620px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  border: 0;
  border-radius: 20px;
}
.income-detail-list {
  display: grid;
  gap: 12px;
}
.income-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
}
.income-total-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: 14px;
  background: rgba(5, 150, 105, 0.1);
  color: #065f46;
}

@media (max-width: 767.98px) {
  .dashboard-hero,
  .kpi-card,
  .dashboard-card {
    border-radius: 18px;
  }

  .kpi-value {
    font-size: 1.15rem;
  }

  .kpi-icon {
    width: 48px;
    height: 48px;
    min-width: 48px;
  }
}
</style>
